import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BonTravailService } from 'src/app/services/bon-travail-service';
import { HeureService } from 'src/app/services/heure-service';
import { KmService } from 'src/app/services/km-service';
import { ReleveHeureEntite } from 'src/app/entites/releve-heures';
import { ReleveKmEntite } from 'src/app/entites/releve-kms';
import { normaliserValeurSaisieReleve } from './saisie-releve';

export interface OptionReleveExistant {
  label: string;
  value: number;
}

/**
 * Saisie d'un relevé d'heures ou de KM : nouvelle valeur ou sélection d'un relevé existant.
 * L'association n'est pas créée en base ici — le parent conserve l'état jusqu'à l'approbation.
 */
@Component({
  selector: 'app-releve-bt-selector',
  templateUrl: './releve-bt-selector.component.html',
  styles: [`
    :host { display: block; width: 100%; }
    .inline-edit__display--editable:hover {
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  `]
})
export class ReleveBtSelectorComponent implements OnInit, OnChanges {

  /** Identifiant du BT existant (pour lister les relevés de l'unité). */
  @Input() bonTravailId?: number;
  /** Identifiant de l'unité (mode création, pour charger les relevés existants). */
  @Input() itemId?: number;
  /** Type de relevé : 'heure' ou 'km'. */
  @Input() type: 'heure' | 'km' = 'heure';
  /** Extrémité : 'debut' ou 'fin'. */
  @Input() cote: 'debut' | 'fin' = 'debut';
  /** Valeur compteur en attente (nouveau relevé). */
  @Input() valeur?: number;
  /** Valeur réelle affichée à côté du compteur (détail BT). */
  @Input() valeurReelle?: number | null;
  /** Valeur actuelle de l'unité, affichée en placeholder (heures ou km au compteur). */
  @Input() placeholder?: number | string | null;
  /** Relevé existant retenu, le cas échéant. */
  @Input() releveLie?: ReleveHeureEntite | ReleveKmEntite;
  /** Mode lecture seule : affiche la valeur ou le relevé lié sans édition. */
  @Input() lectureSeule: boolean = false;
  /** Affiche le libellé du champ (désactiver si un label parent existe déjà). */
  @Input() afficherLabel: boolean = true;
  /** Affiche le texte jusqu'au clic, comme les autres champs du détail BT. */
  @Input() editionAuClic: boolean = false;

  @Output() valeurChange = new EventEmitter<number>();
  /** Émis au blur (et à la sélection) pour la sauvegarde immédiate du détail BT. */
  @Output() valeurSave = new EventEmitter<number>();
  @Output() releveLieChange = new EventEmitter<ReleveHeureEntite | ReleveKmEntite | null>();

  valeurInterne?: number;
  releves: OptionReleveExistant[] = [];
  relevesBruts: Array<ReleveHeureEntite | ReleveKmEntite> = [];
  selectionReleveId: number | null = null;
  modeSelection: boolean = false;
  chargement: boolean = false;
  enEdition: boolean = false;
  private ignorerFermeture: boolean = false;

  constructor(
    private bonTravailService: BonTravailService,
    private heureService: HeureService,
    private kmService: KmService
  ) { }

  ngOnInit(): void {
    this.valeurInterne = this.valeur;
    this.appliquerReleveLie();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['valeur'] && !changes['valeur'].firstChange && !this.enEdition) {
      this.valeurInterne = changes['valeur'].currentValue;
    }
    if (changes['releveLie'] && !changes['releveLie'].firstChange && !this.enEdition) {
      this.appliquerReleveLie();
    }
    if (changes['itemId'] && !changes['itemId'].firstChange) {
      this.releves = [];
      this.relevesBruts = [];
      if (this.modeSelection) {
        this.chargerReleves();
      }
    }
  }

  get unite(): string {
    return this.type === 'heure' ? 'h' : 'km';
  }

  get titreCote(): string {
    return this.cote === 'debut' ? 'Début' : 'Fin';
  }

  get titreLabel(): string {
    const typeLabel = this.type === 'heure' ? 'Heures' : 'KM';
    return `${typeLabel} ${this.titreCote.toLowerCase()} (compteur)`;
  }

  get placeholderTexte(): string {
    return this.placeholder == null || this.placeholder === ''
      ? ''
      : String(this.placeholder);
  }

  get inputId(): string {
    return `releve-bt-${this.type}-${this.cote}`;
  }

  get peutBasculer(): boolean {
    return !!(this.bonTravailId || this.itemId);
  }

  get texteAffichage(): string {
    const compteur = this.valeur == null ? '—' : String(this.valeur);
    const reelles = this.valeurReelle == null ? '—' : String(this.valeurReelle);
    return `${compteur} / ${reelles} ${this.unite}`;
  }

  get texteReleveLie(): string {
    if (!this.releveLie?.date) {
      return 'Relevé lié';
    }
    const date = new Date(this.releveLie.date);
    const dateTexte = !isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : '';
    return dateTexte ? `${dateTexte} — relevé lié` : 'Relevé lié';
  }

  /**
   * Passe en mode saisie au clic, comme les champs inline du détail.
   */
  commencerEdition(): void {
    if (this.lectureSeule || this.enEdition) {
      return;
    }
    this.enEdition = true;
    setTimeout(() => {
      const champ = document.getElementById(this.inputId) as HTMLElement | null;
      champ?.focus?.();
    }, 0);
  }

  /**
   * Empêche le blur de l'input quand on clique le bouton de bascule.
   */
  retenirEdition(event: Event): void {
    event.preventDefault();
    this.ignorerFermeture = true;
    setTimeout(() => this.ignorerFermeture = false, 200);
  }

  /**
   * Ferme l'édition inline après un clic hors de la liste, sans nouvelle sélection.
   */
  surFermetureListe(): void {
    this.fermerEditionSiInline();
  }

  /**
   * Bascule entre saisie d'une nouvelle valeur et sélection d'un relevé existant.
   */
  basculerMode(): void {
    if (this.modeSelection) {
      this.passerEnSaisie();
    } else {
      this.passerEnSelection();
    }
  }

  surChangementValeur(valeur: number): void {
    this.valeurInterne = valeur;
    this.valeurChange.emit(valeur);
  }

  /**
   * Conserve la saisie, y compris sous le compteur courant, puis signale la valeur à persister.
   * La validation des voisins a lieu à l'approbation.
   */
  confirmerValeur(): void {
    this.valeurInterne = normaliserValeurSaisieReleve(this.valeurInterne) as number;
    this.valeurChange.emit(this.valeurInterne);
    this.valeurSave.emit(this.valeurInterne);
    this.fermerEditionSiInline();
  }

  /**
   * Applique immédiatement le relevé choisi dans la liste (sans bouton de confirmation).
   */
  surSelectionReleve(releveId: number | null): void {
    if (releveId == null) {
      this.emettreDeliaison();
      this.fermerEditionSiInline();
      return;
    }
    this.selectionReleveId = releveId;
    this.choisirReleveExistant();
  }

  private fermerEditionSiInline(): void {
    if (!this.editionAuClic || this.ignorerFermeture) {
      return;
    }
    this.enEdition = false;
  }

  private passerEnSelection(): void {
    this.modeSelection = true;
    this.chargerReleves();
  }

  private passerEnSaisie(): void {
    this.modeSelection = false;
    this.selectionReleveId = null;
    this.emettreDeliaison();
  }

  private appliquerReleveLie(): void {
    if (!this.releveLie) {
      this.modeSelection = false;
      this.selectionReleveId = null;
      return;
    }
    this.modeSelection = true;
    this.selectionReleveId = this.identifiantReleve(this.releveLie);
    if (this.releves.length === 0) {
      this.chargerReleves();
    }
  }

  private identifiantReleve(releve: ReleveHeureEntite | ReleveKmEntite): number {
    return this.type === 'heure'
      ? (releve as ReleveHeureEntite).releveHeureId
      : (releve as ReleveKmEntite).releveKmId;
  }

  private valeurReleve(releve: ReleveHeureEntite | ReleveKmEntite): number {
    return this.type === 'heure'
      ? (releve as ReleveHeureEntite).nbHeure
      : (releve as ReleveKmEntite).nbKm;
  }

  chargerReleves(): void {
    this.chargement = true;
    const traiter = (liste: Array<ReleveHeureEntite | ReleveKmEntite>) => {
      this.relevesBruts = liste ?? [];
      this.releves = this.relevesBruts.map(r => ({
        label: this.etiqueterOption(r),
        value: this.identifiantReleve(r)
      }));
      this.chargement = false;
    };
    if (this.bonTravailId) {
      if (this.type === 'heure') {
        this.bonTravailService.ObtenirRelevesItemHeure(this.bonTravailId).subscribe({
          next: res => traiter(res?.valeur ?? []),
          error: () => { this.chargement = false; }
        });
      } else {
        this.bonTravailService.ObtenirRelevesItemKm(this.bonTravailId).subscribe({
          next: res => traiter(res?.valeur ?? []),
          error: () => { this.chargement = false; }
        });
      }
    } else if (this.itemId) {
      if (this.type === 'heure') {
        this.heureService.ObtenirListeParItem(this.itemId).then(res => traiter(res?.valeur ?? [])).catch(() => { this.chargement = false; });
      } else {
        this.kmService.ObtenirListeParItem(this.itemId).then(res => traiter(res?.valeur ?? [])).catch(() => { this.chargement = false; });
      }
    } else {
      this.chargement = false;
    }
  }

  private etiqueterOption(releve: ReleveHeureEntite | ReleveKmEntite): string {
    const date = releve.date ? new Date(releve.date) : null;
    const dateTexte = date && !isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : '';
    const valeurReleve = this.valeurReleve(releve);
    const employe = releve.employe ? ` (${releve.employe.prenom ?? ''} ${releve.employe.nom ?? ''})`.trimEnd() : '';
    const bt = releve.bonTravailNumero
      ? ` — BT ${releve.bonTravailNumero} (${releve.indDebut ? 'début' : 'fin'})`
      : '';
    return `${dateTexte} — ${valeurReleve ?? ''} ${this.unite}${employe}${bt}`;
  }

  private choisirReleveExistant(): void {
    if (this.selectionReleveId == null) {
      return;
    }
    const releve = this.relevesBruts.find(r => this.identifiantReleve(r) === this.selectionReleveId);
    if (!releve) {
      return;
    }
    const valeur = this.valeurReleve(releve);
    this.valeurInterne = valeur;
    this.valeurChange.emit(valeur);
    this.releveLieChange.emit(releve);
    this.fermerEditionSiInline();
  }

  private emettreDeliaison(): void {
    this.releveLieChange.emit(null);
  }
}
